import React, { useState } from 'react';
import { 
  Terminal, 
  Database, 
  Settings, 
  CheckCircle, 
  AlertTriangle,
  Plus,
  Edit,
  Trash,
  Save,
  ChevronDown,
  ChevronRight,
  FileCode,
  Tool,
  Package,
  Coffee,
  Server
} from 'lucide-react';

// Composants UI simplifiés
const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${className}`}>
    {children}
  </div>
);

const CardHeader = ({ children, className = '' }) => (
  <div className={`px-6 py-4 border-b border-gray-200 ${className}`}>
    {children}
  </div>
);

const CardTitle = ({ children, className = '' }) => (
  <h3 className={`text-lg font-semibold text-gray-900 ${className}`}>
    {children}
  </h3>
);

const CardContent = ({ children, className = '' }) => (
  <div className={`px-6 py-4 ${className}`}>
    {children}
  </div>
);
const Button = ({ 
  children, 
  className = '', 
  variant = 'default', 
  size = 'default',
  onClick,
  ...props 
}) => {
  const baseStyles = 'inline-flex items-center justify-center rounded-md font-medium transition-colors';
  const variants = {
    default: 'bg-blue-500 text-white hover:bg-blue-600',
    outline: 'border border-gray-300 bg-white hover:bg-gray-100 text-gray-700',
    ghost: 'hover:bg-gray-100 text-gray-700'
  };
  const sizes = {
    default: 'h-10 px-4 py-2',
    sm: 'h-8 px-3 text-sm',
    lg: 'h-12 px-6'
  };

  return (
    <button
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
      onClick={onClick}
      {...props}
    >
      {children}
    </button>
  );
};


// Installation guide data structure for API externalization
const INSTALLATION_DATA = {
  metadata: {
    title: "Guide d'Installation",
    version: "1.2.0",
    lastUpdate: "2024-03-23",
    environment: "Production",
    author: "Équipe DevOps"
  },
  prerequisites: {
    systems: [
      {
        id: "os",
        name: "Système d'exploitation",
        requirements: [
          "Linux (Ubuntu 22.04 ou plus récent)",
          "macOS (Ventura ou plus récent)",
          "Windows Server 2019 ou plus récent"
        ],
        type: "system"
      },
      {
        id: "hardware",
        name: "Configuration matérielle",
        requirements: [
          "CPU: 4 cœurs minimum",
          "RAM: 8 GB minimum",
          "Stockage: 50 GB disponible"
        ],
        type: "hardware"
      }
    ],
    software: [
      {
        id: "node",
        name: "Node.js",
        version: "18.x ou plus récent",
        installCommand: "curl -fsSL https://deb.nodesource.com/setup_18.x | sudo -E bash - && sudo apt-get install -y nodejs",
        verifyCommand: "node --version",
        type: "runtime"
      },
      {
        id: "docker",
        name: "Docker",
        version: "24.x ou plus récent",
        installCommand: "curl -fsSL https://get.docker.com | sh",
        verifyCommand: "docker --version",
        type: "container"
      },
      {
        id: "postgres",
        name: "PostgreSQL",
        version: "14.x ou plus récent",
        installCommand: "sudo apt-get install -y postgresql-14",
        verifyCommand: "psql --version",
        type: "database"
      }
    ]
  },
  installationSteps: [
    {
      id: "step1",
      title: "Configuration de l'environnement",
      description: "Préparation de l'environnement système",
      commands: [
        {
          description: "Création du répertoire de l'application",
          command: "mkdir -p /opt/myapp && cd /opt/myapp",
          note: "Assurez-vous d'avoir les droits appropriés"
        },
        {
          description: "Configuration des variables d'environnement",
          command: "cp .env.example .env",
          note: "Modifiez les valeurs selon votre environnement"
        }
      ],
      verification: "Vérifiez que le répertoire est créé et que le fichier .env existe",
      troubleshooting: [
        "Vérifier les permissions du répertoire",
        "S'assurer que l'utilisateur a les droits sudo"
      ]
    },
    {
      id: "step2",
      title: "Installation des dépendances",
      description: "Installation des packages et dépendances requises",
      commands: [
        {
          description: "Installation des dépendances système",
          command: "sudo apt-get update && sudo apt-get install -y build-essential",
          note: "Nécessite une connexion Internet"
        },
        {
          description: "Installation des dépendances Node.js",
          command: "npm install",
          note: "Exécuter dans le répertoire du projet"
        }
      ],
      verification: "Vérifiez que toutes les dépendances sont installées correctement",
      troubleshooting: [
        "Vérifier la connexion Internet",
        "Nettoyer le cache npm si nécessaire"
      ]
    },
    {
      id: "step3",
      title: "Configuration de la base de données",
      description: "Mise en place et configuration de PostgreSQL",
      commands: [
        {
          description: "Création de la base de données",
          command: "createdb myapp_db",
          note: "Utiliser l'utilisateur postgres"
        },
        {
          description: "Application des migrations",
          command: "npm run migrate",
          note: "Vérifiez que la base de données est accessible"
        }
      ],
      verification: "Vérifiez que la base de données est créée et les tables sont présentes",
      troubleshooting: [
        "Vérifier les logs PostgreSQL",
        "Vérifier les permissions de l'utilisateur"
      ]
    }
  ],
  postInstallation: [
    {
      id: "verify1",
      title: "Vérification du service",
      command: "npm run test",
      expectedOutput: "All tests passed"
    },
    {
      id: "verify2",
      title: "Démarrage du service",
      command: "npm run start",
      expectedOutput: "Server running on port 3000"
    }
  ]
};

// Component for displaying a command with copy functionality
const CommandBlock = ({ command, description, note }) => {
  const [copied, setCopied] = useState(false);

  const copyCommand = async () => {
    await navigator.clipboard.writeText(command);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="my-4">
      {description && (
        <div className="text-sm text-gray-600 mb-2">{description}</div>
      )}
      <div className="relative">
        <div className="bg-gray-900 text-gray-100 rounded-lg p-4 font-mono text-sm">
          {command}
        </div>
        <button
          onClick={copyCommand}
          className={`absolute right-2 top-2 p-2 rounded-md transition-colors ${
            copied ? 'text-green-400' : 'text-gray-400 hover:text-gray-300'
          }`}
        >
          {copied ? 'Copié!' : 'Copier'}
        </button>
      </div>
      {note && (
        <div className="text-sm text-gray-500 mt-2 flex items-center">
          <AlertTriangle size={14} className="mr-1" /> {note}
        </div>
      )}
    </div>
  );
};

// Component for prerequisites section
const PrerequisitesSection = ({ prerequisites, isEditing, onEdit }) => {
  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-900">Prérequis</h2>
      
      {/* System Requirements */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {prerequisites.systems.map((system) => (
          <Card key={system.id}>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Server className="h-5 w-5 text-blue-500" />
                <span>{system.name}</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {system.requirements.map((req, index) => (
                  <li key={index} className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>{req}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Software Requirements */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Logiciels requis</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {prerequisites.software.map((software) => (
            <Card key={software.id}>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Package className="h-5 w-5 text-blue-500" />
                  <span>{software.name}</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="text-sm text-gray-600">
                    Version: {software.version}
                  </div>
                  <CommandBlock
                    command={software.installCommand}
                    description="Commande d'installation"
                  />
                  <CommandBlock
                    command={software.verifyCommand}
                    description="Vérification de l'installation"
                  />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

// Component for installation steps
const InstallationSteps = ({ steps, isEditing, onEdit }) => {
  return (
    <div className="space-y-8">
      <h2 className="text-xl font-semibold text-gray-900">Étapes d'installation</h2>
      
      {steps.map((step, index) => (
        <Card key={step.id}>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <div className="bg-blue-100 text-blue-600 w-8 h-8 rounded-full flex items-center justify-center">
                {index + 1}
              </div>
              <span>{step.title}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <p className="text-gray-600">{step.description}</p>
              
              {step.commands.map((cmd, cmdIndex) => (
                <CommandBlock
                  key={cmdIndex}
                  command={cmd.command}
                  description={cmd.description}
                  note={cmd.note}
                />
              ))}

              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2">Vérification</h4>
                <p className="text-gray-600">{step.verification}</p>
              </div>

              {step.troubleshooting && step.troubleshooting.length > 0 && (
                <div className="mt-4 p-4 bg-yellow-50 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Dépannage</h4>
                  <ul className="list-disc list-inside space-y-1">
                    {step.troubleshooting.map((item, index) => (
                      <li key={index} className="text-gray-600">{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// Main installation guide component
const DocumentInstallation = () => {
  const [installData, setInstallData] = useState(INSTALLATION_DATA);
  const [isEditing, setIsEditing] = useState(false);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {installData.metadata.title}
          </h1>
          <p className="mt-1 text-gray-600">
            Version {installData.metadata.version} - 
            Dernière mise à jour : {installData.metadata.lastUpdate}
          </p>
        </div>
        <div className="flex space-x-4">
          <Button
            variant="outline"
            onClick={() => setIsEditing(!isEditing)}
            className="flex items-center space-x-2"
          >
            {isEditing ? (
              <>
                <Save className="h-4 w-4" />
                <span>Sauvegarder</span>
              </>
            ) : (
              <>
                <Edit className="h-4 w-4" />
                <span>Modifier</span>
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Main content */}
      <div className="space-y-12">
        {/* Prerequisites Section */}
        <PrerequisitesSection
          prerequisites={installData.prerequisites}
          isEditing={isEditing}
          onEdit={(updated) => {
            setInstallData({
              ...installData,
              prerequisites: updated
            });
          }}
        />

        {/* Installation Steps */}
        <InstallationSteps
          steps={installData.installationSteps}
          isEditing={isEditing}
          onEdit={(updated) => {
            setInstallData({
              ...installData,
              installationSteps: updated
            });
          }}
        />

        {/* Post-Installation Verification */}
        <div className="space-y-6">
          <h2 className="text-xl font-semibold text-gray-900">Vérification post-installation</h2>
          <Card>
            <CardContent className="space-y-4">
              {installData.postInstallation.map((verify) => (
                <div key={verify.id} className="space-y-2">
                  <h3 className="font-medium text-gray-900">{verify.title}</h3>
                  <CommandBlock
                    command={verify.command}
                    note={`Sortie attendue: ${verify.expectedOutput}`}
                  />
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DocumentInstallation;